import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Badge } from '@mantine/core'
import { Page, FilterBar, AdminTable, AmmoScoreBadge } from '../../app/common/ui'
import type { ColumnDef, RowAction } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import type { AdminUserDto } from '../../app/api/agent'
import { format } from 'date-fns'

const TIER_OPTIONS = [
  { label: 'Newcomer', value: 'Newcomer' },
  { label: 'Enthusiast', value: 'Enthusiast' },
  { label: 'Contributor', value: 'Contributor' },
  { label: 'Expert', value: 'Expert' },
  { label: 'Curator', value: 'Curator' },
  { label: 'Armorer', value: 'Armorer' }
]

export default observer(function AdminUsersPage () {
  const { adminStore, userStore } = useStore()
  const history = useHistory()
  const [q, setQ] = useState('')
  const [tier, setTier] = useState<string | undefined>(undefined)
  const [isSuspended, setIsSuspended] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const pageSize = 20

  useEffect(() => {
    if (!userStore.user?.isAdmin) {
      history.replace('/not-found')
      return
    }
    adminStore.loadUsers({
      q: q || undefined,
      tier,
      isSuspended: isSuspended || undefined,
      isAdmin: isAdmin || undefined,
      pageNumber,
      pageSize
    })
  }, [userStore.user?.isAdmin, q, tier, isSuspended, isAdmin, pageNumber, pageSize])

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === 'q') setQ((value as string) ?? '')
    if (key === 'tier') setTier((value as string) ?? undefined)
    if (key === 'isSuspended') setIsSuspended(!!value)
    if (key === 'isAdmin') setIsAdmin(!!value)
    setPageNumber(1)
  }

  const columns: ColumnDef<AdminUserDto>[] = [
    {
      key: 'userName',
      label: 'User',
      render: (row) => (
        <span>
          <a href={`/admin/users/${encodeURIComponent(row.userName)}`} style={{ fontWeight: 500 }}>
            {row.userName}
          </a>
          {row.displayName && (
            <span style={{ marginLeft: 6, color: 'var(--mantine-color-dimmed)', fontSize: 13 }}>
              {row.displayName}
            </span>
          )}
        </span>
      )
    },
    { key: 'email', label: 'Email' },
    {
      key: 'ammoTier',
      label: 'Tier',
      render: (row) => <AmmoScoreBadge tier={row.ammoTier} score={row.ammoScore} size="sm" />
    },
    {
      key: 'isSuspended',
      label: 'Status',
      render: (row) =>
        row.isSuspended
          ? (
          <Badge color="red">Suspended</Badge>
            )
          : (
          <Badge color="green">Active</Badge>
            )
    },
    { key: 'postCount', label: 'Posts' },
    { key: 'partCount', label: 'Parts' },
    { key: 'buildCount', label: 'Builds' },
    {
      key: 'createdOn',
      label: 'Created',
      render: (row) => (row.createdOn ? format(new Date(row.createdOn), 'MMM d, yyyy') : '—')
    }
  ]

  const rowActions: RowAction<AdminUserDto>[] = [
    {
      label: 'View',
      onClick: (row) => history.push(`/admin/users/${encodeURIComponent(row.userName)}`)
    },
    {
      label: 'Suspend',
      onClick: (row) => history.push(`/admin/users/${encodeURIComponent(row.userName)}?suspend=1`),
      color: 'red'
    }
  ]

  const refresh = () =>
    adminStore.loadUsers({
      q: q || undefined,
      tier,
      isSuspended: isSuspended || undefined,
      isAdmin: isAdmin || undefined,
      pageNumber,
      pageSize
    })

  if (!userStore.user?.isAdmin) return null

  const pagination = adminStore.users
    ? {
        totalCount: adminStore.users.totalCount,
        pageNumber: adminStore.users.pageNumber,
        pageSize: adminStore.users.pageSize
      }
    : { totalCount: 0, pageNumber: 1, pageSize: 20 }

  return (
    <Page
      title="User Management"
      actions={<Button onClick={refresh}>Refresh</Button>}
    >
      <FilterBar
        filters={[
          { key: 'q', label: 'Search', type: 'text' },
          { key: 'tier', label: 'Tier', type: 'select', options: TIER_OPTIONS },
          { key: 'isSuspended', label: 'Suspended only', type: 'toggle' },
          { key: 'isAdmin', label: 'Admins only', type: 'toggle' }
        ]}
        onFilterChange={handleFilterChange}
      />
      <div style={{ marginTop: 16 }}>
        <AdminTable<AdminUserDto>
          columns={columns}
          data={adminStore.users?.items ?? []}
          pagination={pagination}
          onPageChange={setPageNumber}
          loading={adminStore.loading}
          rowActions={rowActions}
        />
      </div>
    </Page>
  )
})
